import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type { EntitySchema } from '@/features/list-page/schema/types'

/**
 * Entity schema cache.
 *
 * Difference vs lookupSlice: schemas are structural data that change rarely
 * (only on backend deploys), so there is no TTL. Invalidation is driven by
 * schema_version mismatch — when the backend bumps the version, the next fetch
 * overwrites the cached entry.
 *
 * Persistence: not persisted to localStorage. Schemas are small but version
 * mismatches across releases would be confusing if stale data survived a deploy.
 * In-memory only.
 */

export interface EntitySchemaCacheItem {
  /** null after a successful 404 — caller treats as "unregistered, fall back". */
  schema: EntitySchema | null
  loading: boolean
  error: string | null
}

export interface EntitySchemaState {
  cache: Record<string, EntitySchemaCacheItem>
}

const initialState: EntitySchemaState = { cache: {} }

interface FetchEntitySchemaArg {
  entityType: string
  apiCall: () => Promise<EntitySchema | null>
}

export const fetchEntitySchema = createAsyncThunk<
  { entityType: string; schema: EntitySchema | null },
  FetchEntitySchemaArg,
  { state: { entitySchema: EntitySchemaState } }
>(
  'entitySchema/fetch',
  async ({ entityType, apiCall }) => {
    const schema = await apiCall()
    return { entityType, schema }
  },
  {
    // De-dup concurrent fetches for the same entity. Without this, two
    // useEntitySchema consumers mounting in the same React commit each see
    // loading=false (the pending reducer has not run yet) and fire parallel
    // network calls. RTK's condition runs synchronously before pending.
    condition: ({ entityType }, { getState }) => {
      const entry = getState().entitySchema?.cache?.[entityType]
      return !entry?.loading
    },
  },
)

const slice = createSlice({
  name: 'entitySchema',
  initialState,
  reducers: {
    invalidateEntitySchema: (state, action: PayloadAction<string>) => {
      delete state.cache[action.payload]
    },
    clearAllEntitySchemas: (state) => {
      state.cache = {}
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEntitySchema.pending, (state, action) => {
        const { entityType } = action.meta.arg
        if (!state.cache[entityType]) {
          state.cache[entityType] = { schema: null, loading: true, error: null }
        } else {
          state.cache[entityType].loading = true
          state.cache[entityType].error = null
        }
      })
      .addCase(fetchEntitySchema.fulfilled, (state, action) => {
        const { entityType, schema } = action.payload
        state.cache[entityType] = { schema, loading: false, error: null }
      })
      .addCase(fetchEntitySchema.rejected, (state, action) => {
        const { entityType } = action.meta.arg
        state.cache[entityType] = {
          schema: null,
          loading: false,
          error: action.error.message ?? 'Failed to fetch entity schema',
        }
      })
  },
})

export const { invalidateEntitySchema, clearAllEntitySchemas } = slice.actions
export default slice.reducer

// ── Selectors ───────────────────────────────────────────────────────────────
type RootStateWithEntitySchema = { entitySchema: EntitySchemaState }

export const selectEntitySchema = (
  state: RootStateWithEntitySchema,
  entityType: string,
): EntitySchema | null => state.entitySchema?.cache?.[entityType]?.schema ?? null

export const selectEntitySchemaLoading = (
  state: RootStateWithEntitySchema,
  entityType: string,
): boolean => state.entitySchema?.cache?.[entityType]?.loading ?? false

export const selectEntitySchemaError = (
  state: RootStateWithEntitySchema,
  entityType: string,
): string | null => state.entitySchema?.cache?.[entityType]?.error ?? null

export const selectEntitySchemaVersion = (
  state: RootStateWithEntitySchema,
  entityType: string,
): string | null =>
  state.entitySchema?.cache?.[entityType]?.schema?.schema_version ?? null
